// TerminKontaktePicker.tsx — Kontakte eines Termins (PO 21.09.2026).
//
// Löst den Einzel-`KundePicker` im Termin-Dialog ab: an einem Tisch sitzen
// regelmäßig mehrere Kontakte — die Firma und ihre Grundbesitz-GmbH, die
// Eheleute, der Betrieb und sein Inhaber. Wer den Termin bisher dem zweiten
// Kontakt geben wollte, musste ihn dem ersten wegnehmen.
//
// Der ERSTE Kontakt ist der Hauptkontakt: er landet zusätzlich in `kundeId`
// und trägt damit weiterhin Kalender-Optik, Termin-Art und Fenstertitel. Er
// ist als solcher gekennzeichnet und lässt sich per Klick wechseln.
//
// Gesucht wird über das bestehende `KontaktSuchfeld` (Zuweisen-Suche mit Typ,
// Status und Firmenbezug) — mit `ohneInterne`, das die eigenen Courté-Kontakte
// aus der Trefferliste nimmt: einen Termin dem Kollegen zuzuweisen, der ohnehin
// im Verteiler steht, ergibt keinen Sinn und verdeckt nur den gesuchten Kunden.

import * as React from "react";
import { makeStyles, mergeClasses, shorthands } from "@fluentui/react-components";
import { Dismiss12Regular, Star16Filled, Star16Regular } from "@fluentui/react-icons";
import { REFINED } from "../risikoanalyseStep/tokens";
import { useFormPrimitiveStyles } from "../forms/formPrimitives";
import { KontaktSuchfeld, type KontaktTreffer } from "../forms/widgets/KontaktSuchfeld";
import { kisGruppe, kisKuerzel, kisLabel, trefferIcon } from "../forms/widgets/kontaktTrefferAnzeige";
import type { KontaktStatusGruppe } from "../../services/kontaktSucheService";

/** Ein gewählter Kontakt des Termins. */
export interface TerminKontaktAuswahl {
    id: number;
    name: string;
    /** Firma/AP/Privat/Sonstige — für das Icon am Chip (T-26: statt Typ-Badge). */
    art?: KontaktTreffer["art"];
    /** T-26: K/I/S-Gruppe des Status — nur bekannt, wenn der Kontakt frisch gewählt wurde. */
    statusGruppe?: KontaktStatusGruppe;
}

const useStyles = makeStyles({
    chips: {
        display: "flex",
        flexWrap: "wrap",
        columnGap: "6px",
        rowGap: "6px",
        marginTop: "7px",
    },
    chip: {
        display: "flex",
        alignItems: "center",
        columnGap: "6px",
        ...shorthands.border("1px", "solid", REFINED.line),
        ...shorthands.borderRadius("999px"),
        ...shorthands.padding("4px", "6px", "4px", "10px"),
        backgroundColor: REFINED.white,
        fontFamily: REFINED.fontUI,
        fontSize: "0.86rem",
        color: REFINED.ink,
        maxWidth: "100%",
    },
    chipHaupt: {
        backgroundColor: REFINED.aub50,
        ...shorthands.borderColor(REFINED.aub),
    },
    chipName: {
        whiteSpace: "nowrap",
        overflowX: "hidden",
        textOverflow: "ellipsis",
        maxWidth: "220px",
    },
    /** Stern: macht den Kontakt zum Hauptkontakt (bzw. zeigt, dass er es ist). */
    stern: {
        display: "flex",
        alignItems: "center",
        ...shorthands.borderWidth("0"),
        ...shorthands.padding("0"),
        backgroundColor: "transparent",
        color: REFINED.aub,
        cursor: "pointer",
        lineHeight: "1",
    },
    entfernen: {
        display: "flex",
        alignItems: "center",
        ...shorthands.borderWidth("0"),
        ...shorthands.borderRadius("999px"),
        ...shorthands.padding("3px"),
        backgroundColor: "transparent",
        color: REFINED.ink3,
        cursor: "pointer",
        lineHeight: "1",
        ":hover": { backgroundColor: REFINED.line, color: REFINED.ink },
    },
    /** T-26: Typ-Icon und K/I/S-Pill am Chip — wie in der Trefferliste. */
    icon: { display: "inline-flex", flexShrink: 0, color: REFINED.aub },
    kis: {
        ...shorthands.borderRadius("999px"),
        ...shorthands.padding("1px", "7px"),
        ...shorthands.border("1px", "solid", REFINED.line),
        fontSize: "0.72rem",
        fontWeight: 700,
        color: REFINED.ink2,
        whiteSpace: "nowrap",
    },
    kisKunde: { backgroundColor: REFINED.okBg, color: REFINED.ok, ...shorthands.borderColor(REFINED.okBg) },
    kisInteressent: {
        backgroundColor: REFINED.goldBg,
        color: REFINED.goldInk,
        ...shorthands.borderColor(REFINED.goldLine),
    },
    hinweis: {
        fontFamily: REFINED.fontUI,
        fontSize: "0.78rem",
        color: REFINED.ink3,
        marginTop: "5px",
    },
});

export const TerminKontaktePicker: React.FC<{
    value: TerminKontaktAuswahl[];
    onChange: (kontakte: TerminKontaktAuswahl[]) => void;
    /** Read-only (fremder Kalender ohne WRITE) — Suche und Chips gesperrt. */
    disabled?: boolean;
    /**
     * Feldbeschriftung. Der Termin-Dialog sagt „Kontakte" (PO 27.07.2026: dort
     * wird auch mit Interessenten und Produktpartnern terminiert, „Kunde" ist
     * im KMVS ein Status und nicht die Menge, aus der hier gewählt wird).
     */
    label?: string;
}> = ({ value, onChange, disabled, label = "Kontakte" }) => {
    const f = useFormPrimitiveStyles();
    const s = useStyles();

    const gewaehlteIds = React.useMemo(() => value.map((k) => k.id), [value]);

    const hinzufuegen = (t: KontaktTreffer | null) => {
        if (!t) return;
        if (value.some((k) => k.id === t.contactId)) return;
        onChange([
            ...value,
            { id: t.contactId, name: t.displayName, art: t.art, statusGruppe: kisGruppe(t) },
        ]);
    };

    const entfernen = (id: number) => onChange(value.filter((k) => k.id !== id));

    /** Zum Hauptkontakt machen = an die erste Stelle ziehen. */
    const zuHaupt = (id: number) => {
        const treffer = value.find((k) => k.id === id);
        if (!treffer) return;
        onChange([treffer, ...value.filter((k) => k.id !== id)]);
    };

    return (
        <div className={f.field}>
            {!disabled && (
                <KontaktSuchfeld
                    label={value.length > 0 ? `${label} — weiteren hinzufügen` : label}
                    name="termin-kontakte"
                    placeholder="Name, Kundennummer oder E-Mail"
                    // Immer null: das Feld sammelt, es hält keine Auswahl —
                    // die gewählten Kontakte stehen als Chips darunter (T-266:
                    // unter dem Feld wie bei den Teilnehmern, nicht darüber, wo
                    // sie wie ein weiterer Teilnehmer aussahen).
                    value={null}
                    onChange={hinzufuegen}
                    ohneInterne
                    ausblendenIds={gewaehlteIds}
                />
            )}

            {/* T-266: Ohne Suchfeld (Lese-Kalender) braucht der Chip-Block eine
                eigene Beschriftung — sonst hängen die Chips unbeschriftet unter
                den Teilnehmern und sehen aus wie weitere Teilnehmer. */}
            {disabled && value.length > 0 && <span className={f.label}>{label}</span>}

            {value.length > 0 && (
                <div className={s.chips}>
                    {value.map((k, i) => {
                        const haupt = i === 0;
                        return (
                            <span
                                key={k.id}
                                className={mergeClasses(s.chip, haupt && s.chipHaupt)}
                                title={
                                    haupt
                                        ? `${k.name} — Hauptkontakt`
                                        : `${k.name} — klicken, um zum Hauptkontakt zu machen`
                                }
                            >
                                <button
                                    type="button"
                                    className={s.stern}
                                    disabled={disabled || haupt}
                                    onClick={() => zuHaupt(k.id)}
                                    aria-label={
                                        haupt
                                            ? `${k.name} ist der Hauptkontakt`
                                            : `${k.name} zum Hauptkontakt machen`
                                    }
                                >
                                    {haupt ? <Star16Filled /> : <Star16Regular />}
                                </button>
                                {k.art ? (
                                    <span
                                        className={s.icon}
                                        aria-hidden="true"
                                        data-icon={k.art === "firma" ? "firma" : "person"}
                                    >
                                        {trefferIcon({ art: k.art, contactType: k.art === "firma" ? "Firma" : "Person" })}
                                    </span>
                                ) : null}
                                <span className={s.chipName}>{k.name}</span>
                                {k.statusGruppe ? (
                                    <span
                                        className={mergeClasses(
                                            s.kis,
                                            k.statusGruppe === "kunde" && s.kisKunde,
                                            k.statusGruppe === "interessent" && s.kisInteressent,
                                        )}
                                        aria-label={kisLabel(k.statusGruppe)}
                                        data-kis={kisKuerzel(k.statusGruppe)}
                                    >
                                        {kisKuerzel(k.statusGruppe)}
                                    </span>
                                ) : null}
                                {!disabled && (
                                    <button
                                        type="button"
                                        className={s.entfernen}
                                        onClick={() => entfernen(k.id)}
                                        aria-label={`${k.name} entfernen`}
                                    >
                                        <Dismiss12Regular />
                                    </button>
                                )}
                            </span>
                        );
                    })}
                </div>
            )}

            {value.length > 1 && (
                <span className={s.hinweis}>
                    Der Termin erscheint bei allen {value.length} Kontakten. Der Stern markiert den
                    Hauptkontakt.
                </span>
            )}
        </div>
    );
};
